import { useEffect, useState } from "react";
import {
  Box,
  Chip,
  CircularProgress,
  FormHelperText,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import { factory } from "../api/apiFactory";
import useDebounce from "./hooks/useDebounce";
import { parseSerials } from "../helpers/serials";

const productApi = factory.get("product");

// Serial numbers for one line of an order or invoice.
//
// Serials are always typed or scanned into the box at the top. With a
// productId that product's existing serials (GET /api/v1/product-serials,
// narrowed to userId's holdings when given) are listed below it. Orders pick
// from that list; a purchase invoice passes listOnly, because it is registering
// serials that do not exist yet and the list is only there to show which
// numbers are already taken. They are fetched here rather than through Redux so
// that sibling instances on the same form do not overwrite each other's lists.
// exclude drops serials from that list, e.g. an edited invoice's own serials.
const SerialNumbersInput = ({
  value,
  onChange,
  quantity,
  productId,
  userId,
  listOnly = false,
  exclude = [],
  ...props
}) => {
  const serials = value ?? [];
  const matches = serials.length === quantity;
  const helperText = matches
    ? `${serials.length}/${quantity}`
    : `عدد الارقام التسلسلية ${serials.length} والعدد ${quantity}`;

  const [options, setOptions] = useState([]);
  const [inputValue, setInputValue] = useState("");
  const [loading, setLoading] = useState(false);
  const search = useDebounce(inputValue.trim(), 500);
  const shown = options.filter((s) => !exclude.includes(s));

  useEffect(() => {
    if (!productId) {
      setOptions([]);
      return;
    }
    let cancelled = false;
    setLoading(true);
    productApi
      .serials({
        product_id: productId,
        serial_number: search || undefined,
        user_id: userId || undefined,
      })
      .then((res) => {
        if (cancelled) return;
        setOptions(
          parseSerials(res)
            .map((row) => row.serial_number)
            .filter(Boolean)
        );
      })
      .catch(() => !cancelled && setOptions([]))
      .finally(() => !cancelled && setLoading(false));
    return () => {
      cancelled = true;
    };
  }, [productId, userId, search]);

  // Scanners often append whitespace and people rescan the same box.
  const commit = (next) =>
    onChange([...new Set(next.map((s) => String(s).trim()).filter(Boolean))]);

  const toggle = (serial) =>
    commit(
      serials.includes(serial)
        ? serials.filter((s) => s !== serial)
        : [...serials, serial]
    );

  return (
    <Stack spacing={1} {...props}>
      <TextField
        size="small"
        fullWidth
        value={inputValue}
        label={
          productId
            ? "امسح الباركود او ابحث عن رقم تسلسلي"
            : "امسح الباركود او ادخل الرقم التسلسلي"
        }
        onChange={(e) => setInputValue(e.target.value)}
        onKeyDown={(e) => {
          if (e.key !== "Enter") return;
          // Scanners end with Enter; without this it submits the whole form.
          e.preventDefault();
          if (!inputValue.trim()) return;
          commit([...serials, inputValue]);
          setInputValue("");
        }}
        InputProps={{
          endAdornment: loading ? <CircularProgress size={20} /> : null,
        }}
      />
      {serials.length > 0 ? (
        <Stack direction="row" spacing={1} sx={{ flexWrap: "wrap", rowGap: 1 }}>
          {serials.map((serial) => (
            <Chip
              key={serial}
              label={serial}
              color="primary"
              size="small"
              onDelete={() => toggle(serial)}
            />
          ))}
        </Stack>
      ) : null}
      {productId ? (
        <Box
          sx={{
            maxHeight: 160,
            overflowY: "auto",
            p: 1,
            border: 1,
            borderColor: matches || listOnly ? "divider" : "error.main",
            borderRadius: 1,
          }}
        >
          {listOnly ? (
            <Typography variant="caption" color="text.secondary" component="div">
              الارقام التسلسلية المسجلة لهذا المنتج
            </Typography>
          ) : null}
          {shown.length > 0 ? (
            <Stack
              direction="row"
              spacing={1}
              sx={{ flexWrap: "wrap", rowGap: 1 }}
            >
              {shown.map((serial) => {
                const picked = serials.includes(serial);
                return (
                  <Chip
                    key={serial}
                    label={serial}
                    size="small"
                    color={picked ? "primary" : "default"}
                    variant={picked ? "filled" : "outlined"}
                    onClick={listOnly ? undefined : () => toggle(serial)}
                  />
                );
              })}
            </Stack>
          ) : (
            <Typography variant="body2" color="text.secondary">
              {loading ? "جاري التحميل..." : "لا توجد ارقام تسلسلية متوفرة"}
            </Typography>
          )}
        </Box>
      ) : null}
      <FormHelperText error={!matches}>{helperText}</FormHelperText>
    </Stack>
  );
};

export default SerialNumbersInput;
